import { Descriptions } from 'antd';
import {
  ActionButton,
  DetailLayout,
  ErrorAlertWithDrawer,
  GetApiErrorMessage,
  isLocked,
  LockedBy,
  ObjectHeader,
  ObjectType,
  Padding,
  PopconfirmDelete,
  Spinner,
  useAccessGroupQuery,
  useCheckLockAndNavigate,
  useIsAdmin,
  useMutateAccessGroup,
  useMutationErrors,
  useShowLockData,
  useAlertContext,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AccessGroupDetails.translations.json';
import { AccessGroupBreadcrumbs } from './AccessGroupBreadcrumbs';
import { useParams } from 'react-router-dom';
import { path } from '../../paths';
import { UiLink } from '../../paths/UiLink';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { useCallback } from 'react';

const { addTranslations, useTranslation, t } = i18nNsFactory(
  'access-group-details',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export const AccessGroupDetails = () => {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const accessGroupId = Number(params.id);
  if (isNaN(accessGroupId)) throw new Error('Access group ID not provided');

  const { data: accessGroup } = useAccessGroupQuery({
    id: accessGroupId,
  });

  const { lockData, refetchLockData } = useShowLockData(
    ObjectType.AccessGroup,
    accessGroupId,
  );
  const navigateToIndex = useNavigateTo(path.accessGroupDirectory);
  const navigateToAccessGroupEdit = useNavigateTo(path.accessGroupEdit, {
    id: accessGroupId,
  });
  const checkLockAndNavigateToEdit = useCheckLockAndNavigate(
    refetchLockData,
    lockData,
    navigateToAccessGroupEdit,
  );
  const { setSuccess } = useAlertContext();

  const { mutate } = useMutateAccessGroup();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const handleDelete = useCallback(() => {
    clearMutationErrors();
    mutate(
      {
        id: accessGroupId,
      },
      {
        onSuccess: () => {
          navigateToIndex();
          setSuccess(t('alert.success.deleted'));
        },
        onError: ({ response }) => {
          setMutationErrors(response?.data.errors || []);
        },
      },
    );
  }, [
    clearMutationErrors,
    mutate,
    accessGroupId,
    navigateToIndex,
    setSuccess,
    t,
    setMutationErrors,
  ]);

  const hasPermissionToManage = useIsAdmin();

  if (!accessGroup) {
    return <Spinner />;
  }

  return (
    <DetailLayout
      infoIconUrl={InfoIconUrl.ACCESS_GROUPS_DETAILS}
      type="detail"
      breadCrumb={<AccessGroupBreadcrumbs data={accessGroup} />}
      headerConfig={{
        actions: (
          <>
            <UiLink
              id="viewRelatedInputs-link"
              path={path.inputDataObjectDirectory}
              params={{ accessGroupIds: accessGroup.id, statusCodes: '' }}
              target="_blank"
            >
              {({ type, ...rest }) => (
                <ActionButton
                  label={t('button.view-related-inputs')}
                  layoutType="detail"
                  type="abort"
                  {...rest}
                />
              )}
            </UiLink>
            <UiLink
              id="viewRelatedOutputs-link"
              path={path.outputDataObjectDirectory}
              params={{ accessGroupIds: accessGroup.id, statusCodes: '' }}
              target="_blank"
            >
              {({ type, ...rest }) => (
                <ActionButton
                  label={t('button.view-related-outputs')}
                  layoutType="detail"
                  type="abort"
                  {...rest}
                />
              )}
            </UiLink>
            {hasPermissionToManage && (
              <ActionButton
                disabled={isLocked(lockData)}
                type="mainAction"
                layoutType="detail"
                onClick={() => checkLockAndNavigateToEdit()}
              />
            )}
          </>
        ),
        dropdownMenu: hasPermissionToManage
          ? {
              items: [
                {
                  disabled: isLocked(lockData),
                  key: 'delete',
                  label: (
                    <PopconfirmDelete
                      stopClickPropagation
                      onDelete={handleDelete}
                      genitiveModelName={t('delete.genitive')}
                    />
                  ),
                },
              ],
            }
          : undefined,
      }}
      side={{
        header: (
          <Padding>
            <ObjectHeader lowerText={accessGroup.name} upperPlaceholder="" />
          </Padding>
        ),
        content: (
          <Padding>
            <Descriptions
              column={1}
              items={[
                {
                  key: 'description',
                  label: t('description.label'),
                  children: accessGroup.description,
                },
              ]}
            />
          </Padding>
        ),
        footer: (
          <LockedBy
            id={accessGroup.id}
            entity={accessGroup}
            objectType={ObjectType.AccessGroup}
          />
        ),
      }}
      alert={<ErrorAlertWithDrawer validationMessagesMap={mutationErrors} />}
    />
  );
};
